import {
  getRobloxMetricRatioKey,
  ROBLOX_TOP_CHARTS_CHART_METRIC,
  ROBLOX_TOP_CHARTS_PCU_LABEL_KEY,
  ROBLOX_TOP_CHARTS_TABLE_METRIC_OPTIONS,
} from './robloxTopChartsMetrics';

export interface IRobloxTopChartsSortableColumn {
  columnKey: string;
  metric: string;
  labelKey: string;
  hoverTipsKey?: string;
}

export const ROBLOX_TOP_CHARTS_SORTABLE_COLUMNS: IRobloxTopChartsSortableColumn[] = [
  {
    columnKey: 'pcu',
    metric: ROBLOX_TOP_CHARTS_CHART_METRIC,
    labelKey: ROBLOX_TOP_CHARTS_PCU_LABEL_KEY,
    hoverTipsKey: 'dbi_roblox_top_charts_metric_pcu_tip',
  },
  ...ROBLOX_TOP_CHARTS_TABLE_METRIC_OPTIONS.map((item) => ({
    columnKey: item.value.replace(/^roblox__/, ''),
    metric: item.value,
    labelKey: item.labelKey,
    hoverTipsKey: item.hoverTipsKey,
  })),
];

export const ROBLOX_TOP_CHARTS_DEFAULT_SORT_COLUMN_KEY = 'pcu';

export const getRobloxTopChartsSortListKey = (
  columnKey: string,
  ratioType: 0 | 1,
): string => `${columnKey}_${ratioType}`;

export const ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY = getRobloxTopChartsSortListKey(
  ROBLOX_TOP_CHARTS_DEFAULT_SORT_COLUMN_KEY,
  0,
);

export interface IRobloxTopChartsSortState {
  metric: string;
  ratioType: 0 | 1;
  columnKey: string;
}

export const parseRobloxTopChartsSortListKey = (
  sortKey?: string,
): IRobloxTopChartsSortState => {
  const defaultColumn = ROBLOX_TOP_CHARTS_SORTABLE_COLUMNS.find(
    (item) => item.columnKey === ROBLOX_TOP_CHARTS_DEFAULT_SORT_COLUMN_KEY,
  ) || ROBLOX_TOP_CHARTS_SORTABLE_COLUMNS[0];

  if (!sortKey) {
    return {
      metric: defaultColumn.metric,
      ratioType: 0,
      columnKey: defaultColumn.columnKey,
    };
  }

  const matched = sortKey.match(/^(.+)_(0|1)$/);
  if (!matched) {
    return {
      metric: defaultColumn.metric,
      ratioType: 0,
      columnKey: defaultColumn.columnKey,
    };
  }

  const columnKey = matched[1];
  const ratioType = Number(matched[2]) as 0 | 1;
  const column = ROBLOX_TOP_CHARTS_SORTABLE_COLUMNS.find(
    (item) => item.columnKey === columnKey,
  );

  if (!column) {
    return {
      metric: defaultColumn.metric,
      ratioType: 0,
      columnKey: defaultColumn.columnKey,
    };
  }

  return {
    metric: column.metric,
    ratioType,
    columnKey: column.columnKey,
  };
};

export const buildRobloxTopChartsOrderBy = (
  sortKey: string | undefined,
  sortOrder: 'asc' | 'desc',
): string => {
  const { metric, ratioType } = parseRobloxTopChartsSortListKey(sortKey);
  const orderField = ratioType === 1 ? getRobloxMetricRatioKey(metric) : metric;

  return `${orderField} ${sortOrder}`;
};

export const getRobloxTopChartsChartMetricFromSortKey = (
  sortKey?: string,
): string => parseRobloxTopChartsSortListKey(sortKey).metric;

export const getRobloxTopChartsSortableColumnBySortKey = (
  sortKey?: string,
): IRobloxTopChartsSortableColumn | undefined => {
  const { columnKey } = parseRobloxTopChartsSortListKey(sortKey);

  return ROBLOX_TOP_CHARTS_SORTABLE_COLUMNS.find(
    (item) => item.columnKey === columnKey,
  );
};

export const getRobloxTopChartsChartTitle = (
  sortKey: string | undefined,
  formatMessage: (descriptor: { id: string }) => string,
): string => {
  const sortColumn = getRobloxTopChartsSortableColumnBySortKey(sortKey);
  if (!sortColumn?.labelKey) {
    return formatMessage({ id: 'dbi_roblox_top_charts_metric_section_title' });
  }

  return formatMessage({ id: sortColumn.labelKey });
};
